import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { db } from '../../../firebase';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs } from 'firebase/firestore';

// --- Styled Components ---

const Container = styled.div`
  padding: 2rem;
  background-color: #f4f7f6;
  min-height: 100%;
`;

const Header = styled.h2`
  color: #34495e;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #ecf0f1;
  padding-bottom: 0.5rem;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #ddd;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  margin-right: 10px;
  cursor: pointer;
  border: none;
  background-color: ${props => (props.active ? '#3498db' : 'transparent')};
  color: ${props => (props.active ? 'white' : '#34495e')};
  font-weight: 600;
  border-radius: 5px 5px 0 0;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: ${props => (props.active ? '#2980b9' : '#ecf0f1')};
  }
`;

const SectionContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const Th = styled.th`
  background-color: #34495e;
  color: white;
  padding: 10px;
  text-align: left;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #ecf0f1;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #3498db;
  margin-right: 10px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const DangerButton = styled(ActionButton)`
  color: #e74c3c;
`;

const Form = styled.form`
  display: flex;
  gap: 20px;
  align-items: flex-end;
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #ecf0f1;
  border-radius: 5px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 5px;
`;

const Select = styled.select`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 5px;
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  background-color: #2ecc71;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    background-color: #27ae60;
  }
`;

// --- Types ---

interface Category {
  id: string;
  name: string;
  description: string;
}

interface StallPrice {
  id: string;
  stallType: string;
  price: number;
  currency: string;
  lastUpdated: string;
}

// --- Sub-Components ---

// 1. Category Management Component
const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingDesc, setEditingDesc] = useState('');

  useEffect(() => {
    const categoriesCollectionRef = collection(db, 'categories');
    const unsubscribe = onSnapshot(categoriesCollectionRef, (snapshot) => {
      const fetchedCategories: Category[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as Omit<Category, 'id'>
      }));
      setCategories(fetchedCategories);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching categories:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // CREATE
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      await addDoc(collection(db, 'categories'), {
        name: newCategoryName,
        description: newCategoryDesc,
      });
      setNewCategoryName('');
      setNewCategoryDesc('');
      alert('Category created successfully!');
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Failed to create category.');
    }
  };

  // UPDATE - Start Edit
  const handleStartEdit = (category: Category) => {
    setEditingId(category.id);
    setEditingName(category.name);
    setEditingDesc(category.description);
  };

  // UPDATE - Save Edit
  const handleSaveEdit = async (id: string) => {
    try {
      const categoryDocRef = doc(db, 'categories', id);
      await updateDoc(categoryDocRef, {
        name: editingName,
        description: editingDesc,
      });
      setEditingId(null);
      alert('Category updated successfully!');
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category.');
    }
  };

  // DELETE
  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const categoryDocRef = doc(db, 'categories', id);
        await deleteDoc(categoryDocRef);
        alert('Category deleted!');
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category.');
      }
    }
  };

  return (
    <SectionContent>
      <h3>➕ Create New Category</h3>
      <Form onSubmit={handleCreateCategory}>
        <FormGroup>
          <label htmlFor="cat-name">Category Name</label>
          <Input
            id="cat-name"
            type="text"
            placeholder="e.g., Seasonal Goods"
            value={newCategoryName}
            onChange={e => setNewCategoryName(e.target.value)}
            required
          />
        </FormGroup>
        <FormGroup>
          <label htmlFor="cat-desc">Description (Optional)</label>
          <Input
            id="cat-desc"
            type="text"
            placeholder="Brief description"
            value={newCategoryDesc}
            onChange={e => setNewCategoryDesc(e.target.value)}
          />
        </FormGroup>
        <SubmitButton type="submit">Create Category</SubmitButton>
      </Form>

      <h3>📋 Existing Categories (CRUD)</h3>
      {loading ? (
        <p>Loading categories...</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Description</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id}>
                <Td>
                  {editingId === category.id ? (
                    <Input
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                    />
                  ) : (
                    category.name
                  )}
                </Td>
                <Td>
                  {editingId === category.id ? (
                    <Input
                      value={editingDesc}
                      onChange={e => setEditingDesc(e.target.value)}
                    />
                  ) : (
                    category.description
                  )}
                </Td>
                <Td>
                  {editingId === category.id ? (
                    <>
                      <ActionButton onClick={() => handleSaveEdit(category.id)}>
                        Save
                      </ActionButton>
                      <ActionButton onClick={() => setEditingId(null)}>
                        Cancel
                      </ActionButton>
                    </>
                  ) : (
                    <>
                      <ActionButton onClick={() => handleStartEdit(category)}>
                        Edit
                      </ActionButton>
                      <DangerButton onClick={() => handleDeleteCategory(category.id)}>
                        Delete
                      </DangerButton>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </SectionContent>
  );
};

// 2. Stall Price Management Component
const StallPriceManagement: React.FC = () => {
  const [prices, setPrices] = useState<StallPrice[]>([]);
  const stallTypes = ['Premium', 'Standard'];
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [selectedStallType, setSelectedStallType] = useState<string>('');
  const [newPrice, setNewPrice] = useState<string>('');

  useEffect(() => {
    const pricesCollectionRef = collection(db, 'stallPrices');
    const unsubscribePrices = onSnapshot(pricesCollectionRef, (snapshot) => {
      const fetchedPrices: StallPrice[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as Omit<StallPrice, 'id'>
      }));
      setPrices(fetchedPrices);
      setLoadingPrices(false);
    }, (error) => {
      console.error('Error fetching prices:', error);
      setLoadingPrices(false);
    });

    return () => unsubscribePrices();
  }, []);

  // CREATE/ADJUST Price
  const handleSetPrice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStallType || !newPrice) return;

    const priceValue = parseFloat(newPrice);
    if (isNaN(priceValue) || priceValue <= 0) {
      alert('Please enter a valid price.');
      return;
    }

    const lastUpdated = new Date().toISOString().slice(0, 10);

    // Check if price for this stall type already exists (ADJUST functionality)
    const exists = prices.find(p => p.stallType === selectedStallType);

    try {
      if (exists) {
        // ADJUST existing price
        const priceDocRef = doc(db, 'stallPrices', exists.id);
        await updateDoc(priceDocRef, {
          price: priceValue,
          lastUpdated: lastUpdated,
        });
        alert(`Price for ${selectedStallType} adjusted to ${priceValue} UGX.`);
      } else {
        // CREATE new price
        await addDoc(collection(db, 'stallPrices'), {
          stallType: selectedStallType,
          price: priceValue,
          currency: 'UGX',
          lastUpdated: lastUpdated,
        });
        alert(`New price for ${selectedStallType} created at ${priceValue} UGX.`);
      }

      // Reset form
      setSelectedStallType('');
      setNewPrice('');
    } catch (error) {
      console.error('Error setting price:', error);
      alert('Failed to set price.');
    }
  };

  // Helper to format currency
  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <SectionContent>
      <h3>💰 Set/Adjust Stall Price</h3>
      <Form onSubmit={handleSetPrice}>
        <FormGroup>
          <label htmlFor="price-stall-type">Select Stall Type</label>
          <Select
            id="price-stall-type"
            value={selectedStallType}
            onChange={e => setSelectedStallType(e.target.value)}
            required
          >
            <option value="" disabled>-- Select a Stall Type --</option>
            {stallTypes.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <label htmlFor="new-price">New Price (UGX)</label>
          <Input
            id="new-price"
            type="number"
            placeholder="e.g., 50000"
            value={newPrice}
            onChange={e => setNewPrice(e.target.value)}
            min="1"
            required
          />
        </FormGroup>
        <SubmitButton type="submit">Set/Update Price</SubmitButton>
      </Form>

      <h3>📈 Current Stall Prices</h3>
      {loadingPrices ? (
        <p>Loading prices...</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Stall Type</Th>
              <Th>Current Price</Th>
              <Th>Last Updated</Th>
            </tr>
          </thead>
          <tbody>
            {prices.length > 0 ? (
              prices.map(price => (
                <tr key={price.id}>
                  <Td>{price.stallType}</Td>
                  <Td>
                    <strong>{formatPrice(price.price, price.currency)}</strong>
                  </Td>
                  <Td>{price.lastUpdated}</Td>
                </tr>
              ))
            ) : (
              <tr>
                <Td colSpan={3} style={{ textAlign: 'center' }}>
                  No prices set yet.
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </SectionContent>
  );
};

// --- Main Component ---

const StallConfigurationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'categories' | 'prices'>(
    'categories'
  );

  return (
    <Container>
      <Header>🛍️ Stall Configuration Management</Header>

      <TabContainer>
        <Tab
          active={activeTab === 'categories'}
          onClick={() => setActiveTab('categories')}
        >
          Category Management (CRUD)
        </Tab>
        <Tab
          active={activeTab === 'prices'}
          onClick={() => setActiveTab('prices')}
        >
          Stall Price Management
        </Tab>
      </TabContainer>

      {activeTab === 'categories' && <CategoryManagement />}
      {activeTab === 'prices' && <StallPriceManagement />}
    </Container>
  );
};

export default StallConfigurationManagement;